import { MouseEventHandler, ReactElement, useEffect, useState } from "react";

import { SVG_MUI } from "@ryde/assets";
import { normalizeToArrayValue, ValueType } from "@ryde/shared";

import {
  NumberFieldWrapper,
  TextFieldInputStyled,
  TextFieldLabel,
} from "../number.styled";

interface INumberBetweenFilterFieldProps {
  fieldId: string;
  label: string;
  /** The stored filter value: a `[min, max]` pair, or empty/partial while the user is still typing. */
  value: ValueType | null | undefined;
  /** Called with `[min, max]` once both bounds are typed, or `undefined` while incomplete — clear it. */
  onApply: (bounds: [number, number] | undefined) => void;
  onRemove: () => void;
}

/**
 * The "min / max" range filter chip: label, two number fields and an X to remove. Owns the field state;
 * the caller owns where the value is stored and how it is labelled, so the reports bar (template key)
 * gets a range filter without the grid's custom-field/filterAttributes coupling.
 */
export const NumberBetweenFilterField = ({
  fieldId,
  label,
  value,
  onApply,
  onRemove,
}: INumberBetweenFilterFieldProps): ReactElement => {
  const [minValue, setMinValue] = useState<string>("");
  const [maxValue, setMaxValue] = useState<string>("");

  useEffect(() => {
    // Only null/undefined means an external clear (clear-all writes null). An EMPTY ARRAY is the store
    // round-trip of this field's own incomplete edit (onApply(undefined) → caller stores []) — resetting
    // on it would erase every keystroke while only one bound is typed, making the field impossible to fill.
    if (value === null || value === undefined) {
      setMinValue("");
      setMaxValue("");
      return;
    }

    const stored = normalizeToArrayValue(value);
    if (!stored.length) {
      return;
    }

    const [storedMin, storedMax] = stored;
    setMinValue(
      storedMin === undefined || storedMin === null ? "" : String(storedMin),
    );
    setMaxValue(
      storedMax === undefined || storedMax === null ? "" : String(storedMax),
    );
  }, [value]);

  const applyValue = (nextMin: string, nextMax: string): void => {
    const min = Number(nextMin);
    const max = Number(nextMax);
    const isComplete =
      !!nextMin && !!nextMax && Number.isFinite(min) && Number.isFinite(max);

    onApply(isComplete ? [min, max] : undefined);
  };

  const handleMinChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newValue = event.target.value.replace(/[^0-9.]/g, "");
    setMinValue(newValue);
    applyValue(newValue, maxValue);
  };

  const handleMaxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newValue = event.target.value.replace(/[^0-9.]/g, "");
    setMaxValue(newValue);
    applyValue(minValue, newValue);
  };

  const removeFilter: MouseEventHandler = e => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <NumberFieldWrapper>
      <TextFieldLabel htmlFor={fieldId}>{label}</TextFieldLabel>

      <TextFieldInputStyled
        name={`${fieldId}-min`}
        placeholder="--"
        value={minValue}
        onChange={handleMinChange}
      />

      <TextFieldInputStyled
        name={`${fieldId}-max`}
        placeholder="--"
        value={maxValue}
        onChange={handleMaxChange}
      />

      <SVG_MUI.CircleXMark
        className="circle-x-mark"
        onClick={e => removeFilter(e)}
      />
    </NumberFieldWrapper>
  );
};
